import { useEffect, useRef, useState } from 'react'
import { useSelectedStore } from '../contexts/useSelectedStore.js'

/**
 * Reusable StoreSelector component with multiple variants
 * @param {string} variant - 'icon' | 'full' | 'compact' | 'context' | 'listHeader' - Display variant
 * @param {boolean} showLabel - Whether to show label (for full variant)
 * @param {string} className - Additional CSS classes
 * @param {function} onStoreChange - Optional callback after store changes (runs after context updates)
 */
export default function StoreSelector({
  variant = 'icon',
  showLabel = true,
  className = '',
  onStoreChange,
}) {
  const {
    selectedStore,
    selectStore,
    stores,
    storesLoading,
    storesError,
    defaultStoreId,
  } = useSelectedStore()
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown])

  function handleStoreSelect(store) {
    const next = store ? { id: store.id, name: store.name } : null
    selectStore(next)
    setShowDropdown(false)
    if (onStoreChange) {
      onStoreChange(next)
    }
  }

  const loading = storesLoading

  // Icon variant (header button)
  if (variant === 'icon') {
    return (
      <div ref={dropdownRef} style={{ position: 'relative' }} className={className}>
        <button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setShowDropdown(!showDropdown)
          }}
          style={{
            fontSize: '16px',
            color: 'var(--color-green-600)',
            cursor: 'pointer',
            padding: '8px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid var(--color-green-200)',
            borderRadius: '4px',
            background: 'var(--surface-default)',
            width: '36px',
            height: '36px',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = 'var(--color-green-600)'
            e.target.style.backgroundColor = 'var(--color-green-50)'
            e.target.style.color = 'var(--color-green-700)'
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = 'var(--color-green-200)'
            e.target.style.backgroundColor = 'var(--surface-default)'
            e.target.style.color = 'var(--color-green-600)'
          }}
          aria-label={`Select grocery store${selectedStore ? `: ${selectedStore.name}` : ''}`}
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
          type="button"
          title={
            storesError
              ? 'Store list failed to load'
              : selectedStore?.name || (stores.length === 0 ? 'No Stores' : 'Select Store')
          }
        >
          <i className="fas fa-store" aria-hidden="true"></i>
        </button>
        {showDropdown && renderDropdown()}
      </div>
    )
  }

  // List header (Shop list card): store name + meta, right-aligned; dropdown below
  if (variant === 'listHeader') {
    return (
      <div ref={dropdownRef} className={`shop-store-header-selector ${className}`.trim()}>
        <button
          type="button"
          className="shop-store-name-btn"
          onClick={(e) => {
            e.stopPropagation()
            setShowDropdown(!showDropdown)
          }}
          aria-label={selectedStore ? `Change store (currently ${selectedStore.name})` : 'Choose a store'}
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '11rem' }}>
            {loading ? 'Loading…' : storesError ? 'Store unavailable' : selectedStore?.name || 'Choose store'}
          </span>
          <i className={`fas fa-chevron-${showDropdown ? 'up' : 'down'} shop-store-caret`} aria-hidden="true" />
        </button>
        {showDropdown && renderDropdown('shop-store-dropdown')}
      </div>
    )
  }

  // Context variant (Shop tab - persistent "Shopping at: [Store] [Change]" bar)
  if (variant === 'context') {
    return (
      <div ref={dropdownRef} style={{ position: 'relative' }} className={className}>
        <div
          className="store-context-bar"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-3)',
            background: 'var(--panel, var(--surface-default))',
            border: '1px solid var(--border-subtle, var(--color-green-100))',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-sm)',
          }}
        >
          <span style={{ color: 'var(--text-secondary)' }}>Shopping at:</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setShowDropdown(!showDropdown)
            }}
            className="link-cta link-cta-standalone"
            style={{
              padding: '2px 0',
              minHeight: 'auto',
              fontWeight: '500',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
            aria-label={selectedStore ? `Change store (currently ${selectedStore.name})` : 'Choose a store'}
            aria-expanded={showDropdown}
            aria-haspopup="listbox"
          >
            {loading ? 'Loading…' : storesError ? 'Store unavailable' : selectedStore?.name || 'Choose a store'}
            <i className={`fas fa-chevron-${showDropdown ? 'up' : 'down'}`} aria-hidden="true" style={{ fontSize: 'var(--text-xs)' }} />
          </button>
        </div>
        {showDropdown && renderDropdown('context-dropdown-position')}
      </div>
    )
  }

  // Compact variant (settings menu - link style)
  if (variant === 'compact') {
    return (
      <div ref={dropdownRef} style={{ position: 'relative' }} className={className}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setShowDropdown(!showDropdown)
          }}
          className="settings-option"
          aria-label={`Select grocery store${selectedStore ? `: ${selectedStore.name}` : ''}`}
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
        >
          <i className="fas fa-store" aria-hidden="true"></i> Store:{' '}
          {loading ? 'Loading...' : storesError ? 'Unavailable' : selectedStore?.name || 'Not selected'}
        </button>
        {showDropdown && renderDropdown('settings-dropdown-position')}
      </div>
    )
  }

  // Full variant (preferences page)
  if (variant === 'full') {
    return (
      <div ref={dropdownRef} style={{ position: 'relative' }} className={className}>
        {showLabel && (
          <div className="muted" style={{ fontSize: 'var(--text-xs)', marginBottom: '6px' }}>
            Preferred store
          </div>
        )}
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setShowDropdown(!showDropdown)
            }}
            className="input"
            style={{
              width: '100%',
              textAlign: 'left',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: 'var(--text-xs)',
              minHeight: '44px',
            }}
            aria-label="Select preferred store"
            aria-expanded={showDropdown}
            aria-haspopup="listbox"
            disabled={loading || stores.length === 0}
          >
            <span>
              {loading
                ? 'Loading stores...'
                : storesError
                  ? storesError
                  : selectedStore
                    ? `${selectedStore.name}${defaultStoreId && selectedStore.id === defaultStoreId ? ' (default)' : ''}`
                    : stores.length === 0
                      ? 'No stores available'
                      : 'Select a store'}
            </span>
            <i className={`fas fa-chevron-${showDropdown ? 'up' : 'down'}`} aria-hidden="true" style={{ fontSize: 'var(--text-xs)', marginLeft: '8px' }} />
          </button>
          {showDropdown && renderDropdown('full-dropdown-position')}
        </div>
      </div>
    )
  }

  // Render dropdown menu (shared across variants)
  function renderDropdown(positionClass = '') {
    const isCompact = variant === 'compact'
    const isFull = variant === 'full'
    const isContext = variant === 'context'
    const isListHeader = variant === 'listHeader'
    return (
      <div
        className={positionClass}
        style={{
          position: 'absolute',
          top: '100%',
          right: isCompact || isListHeader ? 0 : isFull || isContext ? 0 : 0,
          left: isFull || isContext ? 0 : 'auto',
          marginTop: '4px',
          backgroundColor: 'var(--surface-default)',
          border: '1px solid var(--color-green-100)',
          borderRadius: '6px',
          boxShadow: 'var(--shadow-md)',
          zIndex: isCompact ? 1000000 : 10001,
          minWidth: isFull ? '100%' : isListHeader ? '220px' : '200px',
          maxWidth: isFull ? '100%' : '300px',
          maxHeight: '300px',
          overflowY: 'auto',
        }}
        role="listbox"
        aria-label="Store selection"
        onClick={(e) => e.stopPropagation()}
      >
        {stores.length === 0 && (
          <div
            style={{
              padding: '8px 12px',
              fontSize: 'var(--text-xs)',
              color: 'var(--text-secondary)',
              fontStyle: 'italic',
            }}
            role="option"
            aria-disabled="true"
          >
            {loading ? 'Loading stores...' : storesError || 'No stores available'}
          </div>
        )}
        {stores.map((s, index) => (
          <div
            key={s.id}
            style={{
              padding: '8px 12px',
              fontSize: 'var(--text-xs)',
              color: selectedStore?.id === s.id ? 'var(--color-green-700)' : 'var(--text-primary)',
              cursor: 'pointer',
              borderBottom: index < stores.length - 1 ? '1px solid var(--color-green-100)' : 'none',
              backgroundColor: selectedStore?.id === s.id ? 'var(--color-green-50)' : 'transparent',
              fontWeight: selectedStore?.id === s.id ? '600' : 'normal',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
            onClick={() => handleStoreSelect(s)}
            onMouseEnter={(e) => {
              if (selectedStore?.id !== s.id) {
                e.target.style.backgroundColor = 'var(--color-green-50)'
                e.target.style.color = 'var(--color-green-700)'
              }
            }}
            onMouseLeave={(e) => {
              if (selectedStore?.id !== s.id) {
                e.target.style.backgroundColor = selectedStore?.id === s.id ? 'var(--color-green-50)' : 'transparent'
                e.target.style.color = selectedStore?.id === s.id ? 'var(--color-green-700)' : 'var(--text-primary)'
              }
            }}
            role="option"
            aria-selected={selectedStore?.id === s.id}
          >
            {selectedStore?.id === s.id && <i className="fas fa-check" aria-hidden="true" style={{ fontSize: 'var(--text-xs)' }} />}
            <span>
              {s.name}
              {defaultStoreId && s.id === defaultStoreId ? ' (default)' : ''}
            </span>
          </div>
        ))}
      </div>
    )
  }
}
